import { useNavigate } from 'react-router-dom'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useState } from 'react'
import { client } from '../../utils/api-client'
import { updateAuthCookie } from '../../utils/tokenManager'
import { useAuth } from '../../context/auth-context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faKey } from '@fortawesome/free-solid-svg-icons'
import { Helmet } from 'react-helmet-async'
import { makeNumbersLatin } from 'utils/farsi'
import { FullPageSpinner } from 'ui/FullPageSpinner'
interface IFormInputs {
  username: string
  password: string
  verificationCode: number
}

interface FromPropTypes {
  pathname?: string
  orderId?: string
  from?: string
  [key: string]: any
}
export const PasswordForm = ({
  username,
  from,
}: {
  username: string
  from: FromPropTypes
}) => {
  const { userLogin, mergeGuestWishlist } = useAuth()
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<IFormInputs>()

  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const onSubmitPassword: SubmitHandler<IFormInputs> = async (options) => {
    setApiError(null)
    setIsLoading(true)
    try {
      const normalizedUsername = makeNumbersLatin(String(username))
      const data = {
        grant_type: 'password',
        username: normalizedUsername,
        password: options.password,
        client_id: '',
        rememberMe: rememberMe,
      }

      const result = await client('oauth/token', { data })

      const authToken = {
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        expiresAt: Date.now() + result.expires_in * 1000, // Convert seconds to milliseconds
      }

      // Use centralized token management
      updateAuthCookie(authToken)

      // Fetch user data
      const userToken = authToken.accessToken
      const userData = await client('customer', { userToken })

      userLogin(userData)

      // Merge guest wishlist if exists
      await mergeGuestWishlist()

      // Navigate based on where user came from
      if (from?.pathname) {
        navigate('/cart/shipping-info', {
          state: { orderId: from?.orderId },
        })
      } else if (from?.from === 'product-request') {
        // Redirect back to main page with form state
        navigate('/', {
          state: from,
        })
      } else {
        navigate('/')
      }
    } catch (error: any) {
      // Try to get a message from the API response
      let message = 'خطا در ورود. رمز کاربری اشتباه است.'
      if (error?.response?.data?.message) {
        message = error.response.data.message
      }
      setApiError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPasswordClick = () => {
    navigate('/forgot-password')
  }

  const handleShowPasswordClick = (e: any) => {
    e.preventDefault()
    setShowPassword(!showPassword)
  }

  if (isLoading) {
    return <FullPageSpinner />
  }

  return (
    <div className='my-12'>
      <Helmet>
        <title className='text-xl font-semibold'>پسورد</title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      <form
        className='max-w-lg w-full mt-5 mx-auto px-4 sm:px-6'
        onSubmit={handleSubmit(onSubmitPassword)}
      >
        <h1 className='text-xl font-semibold text-center pb-2.5 mb-3 '>
          لطفا پسورد خود را وارد کنید
        </h1>
        <div className='relative'>
          <input
            autoFocus
            type={showPassword ? 'text' : 'password'}
            className='input-theme block box-border w-full rounded border border-solid py-3 px-4 pr-12 mb-2.5 text-base sm:text-lg text-left'
            {...register('password', {
              required: 'password is required',
              minLength: {
                value: 8,
                message: 'حداقل تعداد کاراکتر رمز باید 8 باشد',
              },
            })}
            dir='ltr'
          />
          <button
            type='button'
            className='absolute top-1/2 right-3 -translate-y-1/2 transform'
            onClick={handleShowPasswordClick}
            aria-label='نمایش یا پنهان کردن رمز'
          >
            {showPassword ? (
              <FontAwesomeIcon
                icon={faEyeSlash}
                style={{
                  fontSize: 16,
                  marginLeft: 12,
                  color: 'var(--text-primary)',
                }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faEye}
                style={{
                  fontSize: 16,
                  marginLeft: 12,
                  color: 'var(--text-primary)',
                }}
              />
            )}
          </button>
        </div>
        {errors.password && (
          <p className='text-red-600 before:inline before:content-["⚠"]'>
            {' '}
            {errors.password.message}
          </p>
        )}

        {apiError && (
          <p className="text-red-600 before:inline before:content-['⚠'] mb-2 ml-2">
            {apiError}
          </p>
        )}

        <input
          className='w-full rounded text-white border-none mt-8 sm:mt-10 py-3 sm:py-4 px-4 text-base sm:text-lg font-bold tracking-wider active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          type='submit'
          value=' ورود به سایت'
        />
        <div className='flex flex-wrap items-center gap-2 mt-4'>
          <input
            className='mb-0'
            type='checkbox'
            name='remember-me-checkbox'
            checked={rememberMe}
            onChange={() => setRememberMe(!rememberMe)}
          />
          <p className='text-sm sm:text-base font-medium mr-2'>
            مرا به خاطر بسپار
          </p>
        </div>
        <button className='mt-3 sm:mt-4' onClick={handleForgotPasswordClick}>
          <FontAwesomeIcon
            icon={faKey}
            style={{ fontSize: 14, color: 'var(--text-primary)' }}
          />
          <span className='text-base font-medium mr-1'>فراموشی رمز عبور؟</span>
        </button>
      </form>
    </div>
  )
}
